import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';
import { useTranslation } from 'react-i18next';
import { formatPhoneNumber, formatDateTime, getRepairStageDates } from '../lib/utils';
import Footer from '../components/Footer';

type Repair = Database['public']['Tables']['repairs']['Row'];
type Customer = Database['public']['Tables']['customers']['Row'];
type Diagnostic = Database['public']['Tables']['diagnostics']['Row'];
type CompletionRecord = Database['public']['Tables']['completion_records']['Row'];
type RepairStatusHistory = Database['public']['Tables']['repair_status_history']['Row'];
type RepairPhoto = Database['public']['Tables']['repair_photos']['Row'];

type FullRepairDetails = Repair & {
  customer: Customer;
  diagnostic: Diagnostic[];
};

const RepairStatus = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [repair, setRepair] = useState<FullRepairDetails | null>(null);
  const [completionRecord, setCompletionRecord] = useState<CompletionRecord | null>(null);
  const [statusHistory, setStatusHistory] = useState<RepairStatusHistory[] | null>(null);
  const [photos, setPhotos] = useState<RepairPhoto[]>([]);

  useEffect(() => {
    const fetchRepairDetails = async () => {
      if (!id) {
        setError('No repair ID provided');
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('repairs')
          .select(`
            *,
            customer:customers(*),
            diagnostic:diagnostics(*)
          `)
          .eq('repair_id', id)
          .single();

        if (fetchError) throw fetchError;
        if (!data) throw new Error('Repair not found');

        setRepair(data as FullRepairDetails);

        // Fetch completion record (used for fixed timestamp), status history, and photos
        const repairId = (data as any)?.id as string | undefined;
        if (repairId) {
          const [
            { data: crData, error: crError },
            { data: historyData, error: historyError },
            { data: photosData, error: photosError },
          ] = await Promise.all([
            supabase
              .from('completion_records')
              .select('*')
              .eq('repair_id', repairId)
              .maybeSingle(),
            supabase
              .from('repair_status_history')
              .select('*')
              .eq('repair_id', repairId)
              .order('changed_at', { ascending: true }),
            supabase
              .from('repair_photos')
              .select('*')
              .eq('repair_id', repairId)
              .order('created_at', { ascending: true }),
          ]);

          if (!crError) setCompletionRecord(crData || null);
          if (!historyError) setStatusHistory(historyData || null);
          if (!photosError) setPhotos(photosData || []);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load repair details');
      } finally {
        setLoading(false);
      }
    };

    fetchRepairDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse space-y-4 text-center">
          <div className="w-12 h-12 mx-auto">
            <Clock className="w-full h-full text-gray-300" />
          </div>
          <div className="text-gray-500">Loading repair details...</div>
        </div>
      </div>
    );
  }

  if (error || !repair) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-sm max-w-md w-full">
          <div className="flex items-center gap-3 text-red-600 mb-4">
            <AlertCircle className="w-6 h-6 flex-shrink-0" />
            <p className="text-lg">{error || 'Repair not found'}</p>
          </div>
          <a
            href="/"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
          >
            &larr; Return to Customer Intake
          </a>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'picked':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'fixed':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'diagnosed':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'cancelled':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'accepted':
      default:
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'picked':
        return 'Olib ketilgan';
      case 'fixed':
        return 'Tayyor';
      case 'diagnosed':
        return 'Diagnostika qilingan';
      case 'cancelled':
        return 'Bekor qilingan';
      case 'accepted':
        return 'Qabul qilingan';
      default:
        return 'Noma\'lum holat';
    }
  };

  const stageDates = getRepairStageDates(repair, repair.diagnostic, completionRecord, statusHistory);
  const currentStatusDate =
    repair.status === 'diagnosed'
      ? stageDates.diagnosed
      : repair.status === 'fixed'
      ? stageDates.fixed
      : repair.status === 'picked'
      ? stageDates.picked
      : repair.status === 'cancelled'
      ? stageDates.cancelled
      : stageDates.accepted;

  const timelineItems = [
    { key: 'acceptedOn', date: stageDates.accepted },
    { key: 'diagnosedOn', date: stageDates.diagnosed },
    { key: 'fixedOn', date: stageDates.fixed },
    { key: 'pickedOn', date: stageDates.picked },
    { key: 'cancelledOn', date: stageDates.cancelled },
  ].filter((item): item is { key: string; date: string } => Boolean(item.date));

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Header with title and created_at */}
          <div className="p-6 border-b relative">
            <h1 className="text-xl font-bold text-gray-900">Ta'mirlash tavsilotlari</h1>
            <div className="absolute top-6 right-6 text-sm text-gray-500">
              {formatDateTime(repair.created_at)}
            </div>
          </div>

          {/* Main Content - Compact Format as per plan */}
          <div className="p-6 space-y-4">
            {/* 1. Ta'mirlash ID */}
            <div>
              <p className="text-base text-blue-600 font-medium">Ta'mirlash ID: {repair.repair_id}</p>
            </div>

            {/* 2. Customer Name and Phone (2x bigger) */}
            <div>
              <p className="text-2xl text-gray-900 font-medium">
                <a
                  href={`/customer/${repair.customer.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/customer/${repair.customer.id}`);
                  }}
                  className="text-2xl text-blue-600 hover:text-blue-700 hover:underline transition-colors font-medium"
                >
                  {repair.customer.name}
                </a> : <a 
                  href={`tel:${repair.customer.phone}`}
                  className="text-2xl text-blue-600 hover:text-blue-700 hover:underline transition-colors font-medium"
                >
                  {formatPhoneNumber(repair.customer.phone)}
                </a>
              </p>
            </div>

            {/* 3. Device */}
            <div>
              <p className="text-base text-gray-700">
                <span className="font-medium">Qurilma:</span> {repair.device_type}
                {repair.device_model && ` - ${repair.device_model}`}
                {repair.serial_number && ` - ${repair.serial_number}`}
              </p>
            </div>

            {/* 4. Issue - Label on one line, value on new line */}
            <div>
              <p className="text-base text-gray-700 font-medium">Muommo:</p>
              <p className="text-base text-gray-900 mt-1">{repair.issue_description || '—'}</p>
            </div>

            {/* 5. Status */}
            <div>
              <p className="text-base text-gray-700">
                <span className="font-medium">Holati:</span>{' '}
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(repair.status)}`}>
                  {getStatusLabel(repair.status)}
                </span>
              </p>
              <div className="mt-3">
                <p className="text-base text-gray-700 font-medium">{t('currentStatusDate')}:</p>
                <p className="text-base text-gray-900 mt-1">
                  {currentStatusDate ? formatDateTime(currentStatusDate) : formatDateTime(repair.created_at)}
                </p>
              </div>
              {repair.status === 'cancelled' && repair.cancellation_note && (
                <div className="mt-3">
                  <p className="text-base text-gray-700 font-medium">Bekor qilish sababi:</p>
                  <p className="text-base text-gray-900 mt-1 whitespace-pre-wrap">
                    {repair.cancellation_note}
                  </p>
                </div>
              )}
              {timelineItems.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-base text-gray-700 font-medium mb-2">{t('repairTimeline')}:</p>
                  <div className="space-y-2">
                    {timelineItems.map((item) => (
                      <div key={item.key} className="flex items-center justify-between gap-4">
                        <span className="text-sm text-gray-600">{t(item.key)}</span>
                        <span className="text-sm text-gray-900 font-medium">{formatDateTime(item.date)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 6. Repair Photos (internal only) */}
              {photos.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-base text-gray-700 font-medium mb-2">Qurilma rasmlari:</p>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {photos.map((photo) => (
                      <div key={photo.id} className="relative aspect-[2/1]">
                        <img
                          src={photo.image_url}
                          alt="Qurilma rasmi"
                          className="w-full h-full object-cover rounded-lg border border-gray-200"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RepairStatus;